import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { SUPPORTED_LANGUAGES, changeAppLanguage } from '@/app/language';

interface Props {
  className?: string;
  /** 'compact' = globe + code pill (desktop header); 'cell' = icon + code column (mobile action row); 'list' = full-width rows (mobile drawer). */
  variant?: 'compact' | 'cell' | 'list';
}

export default function LanguageSwitcher({ className = '', variant = 'compact' }: Props) {
  const { t, i18n } = useTranslation();
  const current = SUPPORTED_LANGUAGES.find((l) => l.code === i18n.language) ?? SUPPORTED_LANGUAGES[0];

  if (variant === 'list') {
    return (
      <div className={`flex flex-wrap gap-2 ${className}`} role="group" aria-label={t('language.select')}>
        {SUPPORTED_LANGUAGES.map((lang) => (
          <button
            key={lang.code}
            type="button"
            lang={lang.code}
            dir={lang.dir}
            aria-current={lang.code === current.code}
            onClick={() => changeAppLanguage(lang.code)}
            className={`inline-flex items-center justify-center px-3 py-2 rounded-btn-sm border-2 border-ink font-sans text-sm transition-transform duration-200 hover:-translate-y-[1px] ${
              lang.code === current.code ? 'bg-ink text-paper font-bold' : 'bg-paper text-ink'
            }`}
          >
            {lang.nativeLabel}
          </button>
        ))}
      </div>
    );
  }

  return <CompactSwitcher current={current} label={t('language.select')} className={className} cell={variant === 'cell'} />;
}

function CompactSwitcher({
  current,
  label,
  className,
  cell = false,
}: {
  current: (typeof SUPPORTED_LANGUAGES)[number];
  label: string;
  className: string;
  cell?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDocClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <div ref={ref} className={`relative ${className}`}>
      {cell ? (
        <button
          type="button"
          data-fx-mute
          onClick={() => setOpen((o) => !o)}
          aria-haspopup="listbox"
          aria-expanded={open}
          aria-label={label}
          className="flex flex-col items-center justify-end gap-1 w-[42px] pt-1 bg-transparent border-0 appearance-none text-ink"
        >
          <GlobeIcon className="w-[25px] h-[25px]" />
          <span className="text-[8px] leading-none font-mono uppercase tracking-[0.03em] text-ink/70">{current.code}</span>
        </button>
      ) : (
        <button
          type="button"
          data-fx-mute
          onClick={() => setOpen((o) => !o)}
          aria-haspopup="listbox"
          aria-expanded={open}
          aria-label={label}
          className="inline-flex items-center gap-1.5 h-11 px-3 bg-paper border-2 border-ink rounded-btn-sm font-display text-ink transition-transform duration-200 hover:-translate-y-[1px] hover:-translate-x-[1px]"
        >
          <GlobeIcon />
          <span className="text-sm uppercase tracking-wide">{current.code}</span>
        </button>
      )}
      {open && (
        <ul
          role="listbox"
          aria-label={label}
          className="absolute end-0 mt-1 z-[600] min-w-[9rem] bg-paper border-2 border-ink rounded-btn-sm shadow-stamp overflow-hidden"
        >
          {SUPPORTED_LANGUAGES.map((lang) => (
            <li key={lang.code} role="option" aria-selected={lang.code === current.code}>
              <button
                type="button"
                lang={lang.code}
                dir={lang.dir}
                onClick={() => {
                  changeAppLanguage(lang.code);
                  setOpen(false);
                }}
                className={`block w-full text-start px-4 py-2.5 font-sans text-sm border-b border-ink/10 last:border-b-0 ${
                  lang.code === current.code ? 'bg-ink text-paper font-bold' : 'text-ink hover:bg-cream'
                }`}
              >
                {lang.nativeLabel}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function GlobeIcon({ className = 'w-[18px] h-[18px]' }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      className={className}
      aria-hidden="true"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <circle cx="12" cy="12" r="9" />
      <path d="M3 12h18M12 3a14 14 0 0 1 0 18M12 3a14 14 0 0 0 0 18" />
    </svg>
  );
}
